import { type FC, type ReactNode } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded'
import VideoCameraFrontRoundedIcon from '@mui/icons-material/VideoCameraFrontRounded'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { Button } from '../../Common/Button'
import { Checkbox, FilterChip } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import type { AnalysisAssigneeClusterBand, AnalysisCohortSlice, AnalysisIncludedSystemKey } from '../../../types/analysis'

export interface SystemItemDefinition {
  key: AnalysisIncludedSystemKey
  labelKey: NlsKey
  icon: ReactNode
}

export const ANALYSIS_SYSTEMS: SystemItemDefinition[] = [
  { key: 'WORK', labelKey: 'NLSAnalysisSystemWork', icon: <TrackChangesRoundedIcon fontSize="small" /> },
  { key: 'TEAM', labelKey: 'NLSAnalysisSystemTeam', icon: <GroupsRoundedIcon fontSize="small" /> },
  { key: 'MEETINGS', labelKey: 'NLSAnalysisSystemMeetings', icon: <VideoCameraFrontRoundedIcon fontSize="small" /> },
  { key: 'UPLOADS', labelKey: 'NLSAnalysisSystemUploads', icon: <CloudUploadRoundedIcon fontSize="small" /> },
  { key: 'AUTOMATIONS', labelKey: 'NLSAnalysisSystemAutomations', icon: <BoltRoundedIcon fontSize="small" /> },
  { key: 'INTEGRATIONS', labelKey: 'NLSAnalysisSystemIntegrations', icon: <HubOutlinedIcon fontSize="small" /> },
  { key: 'STOCK', labelKey: 'NLSAnalysisSystemStock', icon: <TroubleshootRoundedIcon fontSize="small" /> },
  { key: 'BUDGET', labelKey: 'NLSAnalysisSystemBudget', icon: <PaidRoundedIcon fontSize="small" /> },
  { key: 'FORMS', labelKey: 'NLSAnalysisSystemForms', icon: <DynamicFormRoundedIcon fontSize="small" /> },
  { key: 'GOVERNANCE', labelKey: 'NLSAnalysisSystemGovernance', icon: <PolicyRoundedIcon fontSize="small" /> },
  { key: 'AI', labelKey: 'NLSAnalysisSystemAi', icon: <AutoAwesomeRoundedIcon fontSize="small" /> },
]

export interface AnalysisFilterControlsProps {
  includedSystems: Set<AnalysisIncludedSystemKey>
  onToggleSystem: (system: AnalysisIncludedSystemKey) => void
  onSelectAllSystems: () => void
  onClearAllSystems: () => void
  highlightAnomalies: boolean
  onToggleHighlightAnomalies: () => void
  slice?: AnalysisCohortSlice | null
  roles?: string[]
  onSliceChange?: (slice: AnalysisCohortSlice | null) => void
  sliceDisabled?: boolean
}

export const AnalysisFilterControlsCard: FC<AnalysisFilterControlsProps> = ({
  includedSystems,
  onToggleSystem,
  onSelectAllSystems,
  onClearAllSystems,
  highlightAnomalies,
  onToggleHighlightAnomalies,
  slice = null,
  roles = [],
  onSliceChange = () => {},
  sliceDisabled = false,
}) => {
  const { t } = useNls()

  return (
    <>
      <RailPageShellCard
        className="analysisSystemsCard"
        heading={t('NLSAnalysisIncludedSystemsHeading')}
        eyebrow={t('NLSAnalysisIncludedSystemsEyebrow')}
      >
        <div className="analysisSystemsCard__actions">
          <Button
            variant="tertiary"
            size="sm"
            className="analysisSystemsCard__actionBtn"
            onClick={onSelectAllSystems}
            title={t('NLSAnalysisIncludedSystemsSelectAll')}
          >
            {t('NLSAnalysisIncludedSystemsSelectAll')}
          </Button>
          <span className="analysisSystemsCard__actionDivider">·</span>
          <Button
            variant="tertiary"
            size="sm"
            className="analysisSystemsCard__actionBtn"
            onClick={onClearAllSystems}
            title={t('NLSAnalysisIncludedSystemsClear')}
          >
            {t('NLSAnalysisIncludedSystemsClear')}
          </Button>
        </div>

        <div className="analysisSystemsGrid" role="group" aria-label={t('NLSAnalysisIncludedSystemsHeading')}>
          {ANALYSIS_SYSTEMS.map((system) => {
            const isChecked = includedSystems.has(system.key)
            const label = t(system.labelKey)
            return (
              <FilterChip
                key={system.key}
                selected={isChecked}
                onClick={() => onToggleSystem(system.key)}
                title={t(isChecked ? 'NLSAnalysisSystemChipHide' : 'NLSAnalysisSystemChipShow', { label })}
                className="analysisSystemChip"
              >
                <span className="analysisSystemChip__icon" aria-hidden="true">
                  {system.icon}
                </span>
                <span className="analysisSystemChip__label">{label}</span>
              </FilterChip>
            )
          })}
        </div>
      </RailPageShellCard>

      <RailPageShellCard className="analysisCohortSliceCard" heading={t('NLSAnalysisCohortSliceHeading')} eyebrow={t('NLSAnalysisCohortSliceHeading')}>
        <div className="analysisCohortSliceCard__group" aria-label={t('NLSAnalysisCohortSliceRole')}>
          <span className="analysisCohortSliceCard__label">{t('NLSAnalysisCohortSliceRole')}</span>
          <div className="analysisCohortSliceCard__chips">
            {roles.map((role) => (
              <FilterChip
                key={role}
                disabled={sliceDisabled}
                selected={slice?.kind === 'ROLE' && slice.value === role}
                onClick={() => onSliceChange(slice?.kind === 'ROLE' && slice.value === role ? null : { kind: 'ROLE', value: role })}
                className="analysisSystemChip"
              >
                {role}
              </FilterChip>
            ))}
          </div>
        </div>
        <div className="analysisCohortSliceCard__group" aria-label={t('NLSAnalysisCohortSliceCluster')}>
          <span className="analysisCohortSliceCard__label">{t('NLSAnalysisCohortSliceCluster')}</span>
          <div className="analysisCohortSliceCard__chips">
            {(['HIGH', 'MEDIUM', 'LOW', 'IDLE'] as AnalysisAssigneeClusterBand[]).map((band) => (
              <FilterChip
                key={band}
                disabled={sliceDisabled}
                selected={slice?.kind === 'ASSIGNEE_CLUSTER' && slice.value === band}
                onClick={() => onSliceChange(slice?.kind === 'ASSIGNEE_CLUSTER' && slice.value === band ? null : { kind: 'ASSIGNEE_CLUSTER', value: band })}
                className="analysisSystemChip"
              >
                {t(`NLSAnalysisCohortSliceCluster${band[0]}${band.slice(1).toLowerCase()}` as 'NLSAnalysisCohortSliceClusterHigh')}
              </FilterChip>
            ))}
          </div>
        </div>
        {slice && !sliceDisabled && (
          <FilterChip className="analysisSystemChip" onClick={() => onSliceChange(null)}>
            {t('NLSAnalysisCohortSliceClearAction')}
          </FilterChip>
        )}
        {sliceDisabled && <small className="analysisCohortSliceCard__disabled">{t('NLSAnalysisCohortSliceCompareDisabled')}</small>}
      </RailPageShellCard>

      <RailPageShellCard
        className="analysisAnomalyCard"
        heading={t('NLSAnalysisHighlightAnomaliesHeading')}
        eyebrow={t('NLSAnalysisHighlightAnomaliesEyebrow')}
      >
        <Checkbox
          wrapperClassName="analysisAnomalyToggleRow"
          checked={highlightAnomalies}
          onChange={() => onToggleHighlightAnomalies()}
          aria-label={t('NLSAnalysisHighlightAnomaliesHeading')}
          label={(
            <div className="analysisAnomalyToggleRow__content">
              <span className="analysisAnomalyToggleRow__title">
                {t('NLSAnalysisHighlightAnomaliesHeading')}
              </span>
              <small className="analysisAnomalyToggleRow__desc">
                {t('NLSAnalysisHighlightAnomaliesDesc')}
              </small>
            </div>
          )}
        />
      </RailPageShellCard>
    </>
  )
}
